/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useMemo } from 'react';

import { Typography2 } from '@coursera/cds-core';

import type { CarouselCollection } from 'bundles/collections-carousel/types/CarouselCollection';
import CollectionModule from 'bundles/page-config-common/components/shared/CollectionModule';
import HomeSection from 'bundles/page-config-common/components/shared/HomeSection';
import { useHeaderLevel } from 'bundles/page-config-common/contexts/HeaderLevelContext';
import { useEnterpriseCollections } from 'bundles/page-config-common/contexts/enterprise/EnterpriseCollectionsContext';
import { ENTERPRISE_COLLECTION_TYPES } from 'bundles/unified-home-common/constants';

import _t from 'i18n!nls/consumer-home';

const styles = {
  enterpriseCollectionHeader: css`
    margin-bottom: var(--cds-spacing-300);
  `,
};

export const filterForCreditCollection = (
  enterpriseCollections: CarouselCollection[] | undefined,
  titleComponent: 'h3' | 'h4'
) => {
  const forCreditCollection = enterpriseCollections?.find(
    (collection) => collection?.collectionType === ENTERPRISE_COLLECTION_TYPES.FOR_CREDIT
  );

  return (
    forCreditCollection && {
      ...forCreditCollection,
      entities: forCreditCollection?.entities?.map((entity) => ({
        ...entity,
        titleComponent,
      })),
    }
  );
};

const EnterpriseForCreditModuleRenderer = () => {
  const { isEnterpriseCollectionsLoading, enterpriseCollections } = useEnterpriseCollections();
  const { currentLevel, nextLevel } = useHeaderLevel();

  const forCreditCollection = useMemo(
    () => filterForCreditCollection(enterpriseCollections, nextLevel as 'h3' | 'h4'),
    [enterpriseCollections, nextLevel]
  );

  if (!isEnterpriseCollectionsLoading && (!forCreditCollection || forCreditCollection?.entities?.length === 0)) {
    return null;
  }

  return (
    <HomeSection className="PageConfig_EnterpriseForCreditCollectionComponent">
      <CollectionModule
        isCollectionLoading={isEnterpriseCollectionsLoading}
        collection={forCreditCollection}
        customHeader={
          <div css={styles.enterpriseCollectionHeader}>
            <Typography2 variant="titleSmall" component={currentLevel}>
              {_t('Earn credit towards your degree with these Coursera courses')}
            </Typography2>
            <Typography2 component="p" variant="bodyPrimary">
              {_t('View the courses offered for a limited time and offered for credit by your organization.')}
            </Typography2>
          </div>
        }
      />
    </HomeSection>
  );
};

export default EnterpriseForCreditModuleRenderer;
